<template>
  <div class="history-with-employee">
    <b-card>
      <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between align-items-sm-center">
        <div class="d-flex align-items-center">
          <b-avatar :src="employee ? employee.avatar : null" size="64" class="mr-1" />
          <div>
            <h1 class="mb-0">History with {{ employee ? employee.name : "..." }}</h1>
            <small v-if="employee && employee.designation" class="text-muted">{{ employee.designation }}</small>
          </div>
        </div>
        <b-button variant="primary" class="mt-1 mt-sm-0" @click="formVisible = true">
          <feather-icon icon="PlusIcon" class="mr-50" />
          New Note
        </b-button>
      </div>
      <p class="text-muted mb-0 mt-1">Your own notes that mention this person, newest first.</p>
    </b-card>

    <div v-if="!isBusy && notes.length === 0" class="misc-inner p-2 p-sm-3">
      <div class="w-100 text-center">
        <h4 class="mb-1">No notes yet</h4>
        <p class="mb-0">You haven't written anything about this person yet.</p>
      </div>
    </div>

    <b-card v-else>
      <div ref="scrollContainer">
        <app-timeline class="mt-1">
          <app-timeline-item
            v-for="note in notes"
            :key="note.hashid"
            icon="MessageSquareIcon"
            variant="primary"
            class="history-timeline-item"
          >
            <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
              <h6 v-if="note.title" class="mb-0">{{ note.title }}</h6>
              <small class="text-muted ml-auto">{{ formatDate(note.noted_at) }}</small>
            </div>

            <div v-if="note.tags && note.tags.length" class="mb-50">
              <b-badge
                v-for="(tag, index) in note.tags"
                :key="index"
                pill
                variant="light-primary"
                class="mr-50 mb-50"
              >
                {{ tag }}
              </b-badge>
            </div>

            <div class="note-content" v-html="note.content"></div>
          </app-timeline-item>
        </app-timeline>

        <div v-if="isBusy" class="text-center py-2">
          <b-spinner variant="primary" small />
        </div>
      </div>
    </b-card>

    <PersonalNoteForm
      :visible.sync="formVisible"
      :note="null"
      :employee-list-options="employeeListOptions"
      :default-subject-id="employee ? employee.hashid : null"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import { BCard, BButton, BBadge, BSpinner, BAvatar } from "bootstrap-vue";
import axios from "@axios";
import { onUnmounted } from "@vue/composition-api";
import store from "@/store";
import employeeStoreModule from "../employees/employeeStoreModule";
import AppTimeline from "@core/components/app-timeline/AppTimeline.vue";
// Deliberately the clean, uncoupled component -- this project's regular
// AppTimelineItem.vue has been customized for interview feedback (requires
// an `item` prop, pulls in FeedBackView) which has no place in a personal
// notes timeline. AppTimelineItemOrignal.vue is the pristine Vuexy
// app-timeline-item with just variant/title/subtitle/time/icon, matching
// the real bundled demo at src/components/timeline/TimelineCustomContentWithIcon.vue
// (the title/time/tags/content layout below is copied from that demo).
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItemOrignal.vue";
import PersonalNoteForm from "./PersonalNoteForm.vue";

export default {
  name: "HistoryWithEmployee",
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
    BAvatar,
    AppTimeline,
    AppTimelineItem,
    PersonalNoteForm,
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = "app-employees";

    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
      store.registerModule(USER_APP_STORE_MODULE_NAME, employeeStoreModule);
    }

    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(USER_APP_STORE_MODULE_NAME);
      }
    });
  },
  data() {
    return {
      employee: null,
      notes: [],
      employeeListOptions: [],
      currentPage: 1,
      lastPage: 1,
      isBusy: false,
      formVisible: false,
    };
  },
  computed: {
    hasMore() {
      return this.currentPage < this.lastPage;
    },
    employeeId() {
      return this.$route.params.employeeId;
    },
  },
  created() {
    this.fetchEmployee();
    this.fetchEmployeeList();
    this.fetchHistory(true);
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      if (this.isBusy || !this.hasMore) return;
      const scrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
      if (scrolledToBottom) {
        this.loadMore();
      }
    },
    fetchEmployee() {
      this.$store
        .dispatch("app-employees/fetchEmployee", this.employeeId)
        .then((res) => {
          this.employee = res.data.data;
        })
        .catch(() => {});
    },
    fetchEmployeeList() {
      this.$store
        .dispatch("app/get", { url: "list/user-list-for-tag-names" })
        .then((res) => {
          this.employeeListOptions = res.data.data;
        })
        .catch(() => {});
    },
    fetchHistory(reset) {
      if (this.isBusy) return;

      if (reset) {
        this.currentPage = 1;
        this.notes = [];
        this.lastPage = 1;
      }
      this.isBusy = true;
      axios
        .get(`/personal-notes/history/${this.employeeId}`, {
          params: { page: this.currentPage },
        })
        .then((res) => {
          if (reset) {
            this.notes = res.data.data;
          } else {
            // vue-infinite-scroll can fire its scroll handler more than
            // once for the same threshold crossing (it re-attaches a
            // listener on every re-render) -- dedupe by hashid so a
            // double-fire never appends the same note twice.
            const seen = new Set(this.notes.map((n) => n.hashid));
            this.notes = this.notes.concat(res.data.data.filter((n) => !seen.has(n.hashid)));
          }
          this.lastPage = res.data.meta.last_page;
          this.currentPage = res.data.meta.current_page;
          this.isBusy = false;
        })
        .catch(() => {
          this.isBusy = false;
        });
    },
    loadMore() {
      if (!this.hasMore || this.isBusy) return;
      this.currentPage += 1;
      this.fetchHistory(false);
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    handleSaved() {
      this.fetchHistory(true);
    },
  },
};
</script>

<style lang="scss" scoped>
.note-content {
  word-break: break-word;
}
.history-timeline-item {
  padding-bottom: 1.5rem;
}
</style>
